import { useEffect, useState, useCallback, useRef } from "react";
import { UploadTracker } from "./UploadTracker";
import { toast } from "sonner";
import { Fuel, Upload, Loader2, Trash2, ChevronDown, Car, Droplets, Gauge, Search, ArrowUpDown, ArrowUp, ArrowDown, Settings2 } from "lucide-react";
import { FuelSettingsPanel } from "./FuelSettingsPanel";
import { Button } from "./ui/button";
import { BulkTools } from "./BulkTools";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import { Money } from "./SaudiRiyal";
import { useLang } from "../i18n/LanguageContext";
import { FleetFuelCost } from "./FleetFuelCost";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { FuelReportPanel } from "./FuelReportPanel";
import { AnnouncementsBar } from "./AnnouncementsBar";
import { FuelPlateMatchDialog } from "./FuelPlateMatchDialog";
import { Link2 } from "lucide-react";
import { WaeiAnalysisPanel } from "./WaeiAnalysisPanel";
import { FuelPayrollDeduction } from "./FuelPayrollDeduction";
import { VehicleFuelReport } from "./VehicleFuelReport";
import { personMatch } from "../lib/searchText";

const FGRID = "grid items-center grid-cols-[2fr_1.4fr_0.9fr_0.9fr_1.2fr_40px]";

export const FuelPanel = ({ canEdit }) => {
  const { t, dir } = useLang();
  const money = (n) => <Money value={n} />;
  const moneyTxt = (n) => `${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })} ${t("dash.common.riyal")}`;
  const [data, setData] = useState(null);
  const [month, setMonth] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const [provider, setProvider] = useState("");
  const [day, setDay] = useState("");
  const [uploadProvider, setUploadProvider] = useState("سيارة اب");
  const [uploadDate, setUploadDate] = useState("");
  const [matchOpen, setMatchOpen] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async (m, p, d) => {
    try { const data = await api.fuelOverview(m, p, d); setData(data); if (!m && data.month) setMonth(data.month); }
    catch { toast.error(t("dash.fuel.loadFail")); }
  }, [t]);
  useEffect(() => { load(month || undefined, provider || undefined, day || undefined); }, [month, provider, day, load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const r = await api.importFuel(file, uploadProvider, uploadDate || undefined);
      toast.success(t("dash.fuel.imported", { n: r.matched, p: uploadProvider, total: moneyTxt(r.total_amount) }));
      if (r.unmatched) toast.info(t("dash.fuel.unmatched", { n: r.unmatched }) + (r.unmatched_names?.length ? `: ${r.unmatched_names.slice(0, 4).join("، ")}` : ""));
      if (r.custody_linked || r.still_unmatched || r.unknown_plates) {
        const msg = `ربط تلقائي من العهد: ${r.custody_linked || 0} سجل · غير مربوط: ${r.still_unmatched || 0} · لوحات غير معروفة: ${r.unknown_plates || 0}`;
        toast[r.unknown_plates ? "warning" : "info"](msg, { duration: 12000, ...(r.unknown_plates ? { action: { label: "عرض وإضافة", onClick: () => setMatchOpen(true) } } : {}) });
      }
      setMonth(""); load(undefined, provider || undefined);
    } catch (err) { toast.error(err?.response?.data?.detail || t("dash.fuel.uploadFail")); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const delMonth = async () => {
    if (!month || !window.confirm(t("dash.fuel.confirmDel", { m: month }))) return;
    try { const r = await api.deleteFuelMonth(month); toast.success(t("dash.fuel.deleted", { n: r.deleted })); load(month, provider || undefined); }
    catch (err) { toast.error(err?.response?.data?.detail || t("dash.fuel.delFail")); }
  };

  const riders = data?.riders || [];
  const [q, setQ] = useState("");
  const [fMatch, setFMatch] = useState("");
  const [sortKey, setSortKey] = useState("total");
  const [sortDir, setSortDir] = useState("desc");
  const norm = (s) => (s || "").toString().toLowerCase();
  const sortVal = (g) => {
    switch (sortKey) {
      case "name": return norm(g.name);
      case "vehicle": return norm(g.vehicle_number);
      case "count": return g.count || 0;
      case "liters": return g.liters || 0;
      default: return g.total || 0;
    }
  };
  const viewRiders = [...riders.filter((g) => (!q.trim() || personMatch(g, q))
    && (!fMatch || (fMatch === "yes" ? g.matched : !g.matched)))].sort((a, b) => {
    const va = sortVal(a), vb = sortVal(b);
    if (va < vb) return sortDir === "asc" ? -1 : 1;
    if (va > vb) return sortDir === "asc" ? 1 : -1;
    return 0;
  });
  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir(k === "name" || k === "vehicle" ? "asc" : "desc"); }
  };
  const SortIcon = ({ k }) => sortKey !== k
    ? <ArrowUpDown className="h-3 w-3 opacity-40" />
    : sortDir === "asc" ? <ArrowUp className="h-3 w-3 text-emerald-600" /> : <ArrowDown className="h-3 w-3 text-emerald-600" />;

  return (
    <div className="space-y-4" data-testid="fuel-panel" dir={dir}>
      <Tabs defaultValue="cost" className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="cost" data-testid="fuel-tab-cost">{t("dash.fuel.title")}</TabsTrigger>
          <TabsTrigger value="report" data-testid="fuel-tab-report" className="gap-1.5"><Gauge className="h-4 w-4" /> استهلاك الوقود</TabsTrigger>
          <TabsTrigger value="vehicles" data-testid="fuel-tab-vehicles" className="gap-1.5"><Car className="h-4 w-4" /> وقود المركبات</TabsTrigger>
          <TabsTrigger value="waei" data-testid="fuel-tab-waei" className="gap-1.5"><Car className="h-4 w-4" /> تحليل واعي</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="fuel-tab-payroll" className="gap-1.5"><Droplets className="h-4 w-4" /> خصم من الراتب</TabsTrigger>
          <TabsTrigger value="settings" data-testid="fuel-tab-settings" className="gap-1.5"><Settings2 className="h-4 w-4" /> إعدادات البنزين</TabsTrigger>
        </TabsList>
        <TabsContent value="cost" className="space-y-4">
          {month && <UploadTracker params={{ section: "fuel", month }} rev={data} title="رفع ملفات البنزين" testid="ut-fuel" canEdit={canEdit} onDeleted={() => load(month)} />}
          <AnnouncementsBar page="fuel" />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-heading font-bold text-slate-800 flex items-center gap-2"><Fuel className="h-5 w-5 text-emerald-600" /> {t("dash.fuel.title")}</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input data-testid="fuel-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="بحث بالاسم أو رقم المركبة" className="h-9 w-52 pr-8" />
          </div>
          <select data-testid="fuel-match-filter" value={fMatch} onChange={(e) => setFMatch(e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm">
            <option value="">كل المناديب</option><option value="yes">مربوط بموظف</option><option value="no">غير مربوط</option>
          </select>
          <select data-testid="fuel-provider-filter" value={provider} onChange={(e) => setProvider(e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm">
            <option value="">{t("dash.fuel.allProviders")}</option>
            {(data?.providers || []).map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select data-testid="fuel-month-select" value={month} onChange={(e) => { setMonth(e.target.value); setDay(""); }}
            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm">
            {(data?.months || []).length === 0 && <option value="">—</option>}
            {(data?.months || []).map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select data-testid="fuel-day-select" value={day} onChange={(e) => setDay(e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm" title="عرض يوم محدد">
            <option value="">كل أيام الشهر</option>
            {(data?.days || []).map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          {canEdit && (
            <>
              <select data-testid="fuel-upload-provider" value={uploadProvider} onChange={(e) => setUploadProvider(e.target.value)}
                className="h-9 rounded-md border border-emerald-200 bg-emerald-50 px-3 text-sm text-emerald-700">
                <option value="سيارة اب">سيارة اب</option>
                <option value="بترو اب">بترو اب</option>
                <option value="واعي">واعي</option>
              </select>
              <Input type="date" data-testid="fuel-upload-date" value={uploadDate} onChange={(e) => setUploadDate(e.target.value)}
                title="يوم الشيت (اختياري — للرفع اليومي)"
                className="h-9 rounded-md border border-emerald-200 bg-emerald-50 px-2 text-sm text-emerald-700 w-auto" />
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={onFile} className="hidden" data-testid="fuel-file-input" />
              <Button data-testid="fuel-upload-btn" disabled={busy} onClick={() => fileRef.current?.click()} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5 h-9">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {t("dash.fuel.uploadBtn")}
              </Button>
              <BulkTools section="fuel" onDone={() => load(month || undefined, provider || undefined)} />
              {data?.count > 0 && (
                <Button data-testid="fuel-delete-month-btn" onClick={delMonth} variant="outline" className="gap-1.5 h-9 border-rose-200 text-rose-600 hover:bg-rose-50">
                  <Trash2 className="h-4 w-4" /> {t("dash.fuel.delMonth")}
                </Button>
              )}
              {data?.count > 0 && (
                <Button data-testid="fuel-match-plates-btn" onClick={() => setMatchOpen(true)} variant="outline" className="gap-1.5 h-9 border-sky-200 text-sky-700 hover:bg-sky-50">
                  <Link2 className="h-4 w-4" /> مطابقة اللوحات
                  {riders.filter((r) => !r.matched).length > 0 && (
                    <span className="rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold px-1.5 py-0.5" data-testid="fuel-match-badge">{riders.filter((r) => !r.matched).length}</span>
                  )}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div role="button" tabIndex={0} data-testid="fuel-kpi-total" onClick={() => { setSortKey("total"); setSortDir("desc"); }} className="cursor-pointer rounded-2xl border border-emerald-100 bg-emerald-50 p-4 transition hover:shadow-md">
          <p className="text-xs text-emerald-700">{t("dash.fuel.totalCost", { m: data?.day || data?.month || "—" })}</p>
          <p className="font-heading text-2xl font-bold text-emerald-700 font-mono-nums mt-1">{money(data?.total)}</p>
        </div>
        <div role="button" tabIndex={0} data-testid="fuel-kpi-ops" onClick={() => { setSortKey("count"); setSortDir("desc"); }} className="cursor-pointer rounded-2xl border border-slate-200 bg-white p-4 transition hover:shadow-md">
          <p className="text-xs text-slate-500">{t("dash.fuel.opsCount")}</p>
          <p className="font-heading text-2xl font-bold text-slate-800 font-mono-nums mt-1">{data?.count || 0}</p>
        </div>
        <div role="button" tabIndex={0} data-testid="fuel-kpi-riders" onClick={() => { setQ(""); setFMatch(""); setSortKey("name"); setSortDir("asc"); }} className="cursor-pointer rounded-2xl border border-slate-200 bg-white p-4 transition hover:shadow-md">
          <p className="text-xs text-slate-500">{t("dash.fuel.ridersCount")}</p>
          <p className="font-heading text-2xl font-bold text-slate-800 font-mono-nums mt-1">{riders.length}</p>
        </div>
      </div>

      <FleetFuelCost month={month} rev={data} />

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-[640px]">
            <div className={`${FGRID} bg-slate-50 text-slate-500 text-xs`}>
              <button type="button" data-testid="fuel-sort-name" onClick={() => toggleSort("name")} className="px-3 py-2.5 font-semibold text-right flex items-center gap-1 hover:text-slate-700">{t("dash.fuel.colRider")} <SortIcon k="name" /></button>
              <button type="button" data-testid="fuel-sort-vehicle" onClick={() => toggleSort("vehicle")} className="px-3 py-2.5 font-semibold text-right flex items-center gap-1 hover:text-slate-700">{t("dash.fuel.colVehicle")} <SortIcon k="vehicle" /></button>
              <button type="button" data-testid="fuel-sort-count" onClick={() => toggleSort("count")} className="px-3 py-2.5 font-semibold flex items-center justify-center gap-1 hover:text-slate-700">{t("dash.fuel.colOps")} <SortIcon k="count" /></button>
              <button type="button" data-testid="fuel-sort-liters" onClick={() => toggleSort("liters")} className="px-3 py-2.5 font-semibold flex items-center justify-center gap-1 hover:text-slate-700">{t("dash.fuel.colLiters")} <SortIcon k="liters" /></button>
              <button type="button" data-testid="fuel-sort-total" onClick={() => toggleSort("total")} className="px-3 py-2.5 font-semibold flex items-center justify-center gap-1 hover:text-slate-700">{t("dash.fuel.colMonthTotal")} <SortIcon k="total" /></button>
              <div className="px-3 py-2.5"></div>
            </div>
            {!data ? (
              <div className="px-3 py-10 text-center text-slate-300"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
            ) : viewRiders.length === 0 ? (
              <div className="px-3 py-10 text-center text-slate-400">{q.trim() || fMatch ? "لا نتائج مطابقة للبحث" : t("dash.fuel.noData")}</div>
            ) : viewRiders.map((g, i) => {
              const rowKey = g.employee_id || `${g.name}|${g.vehicle_number}|${i}`;
              return (
              <div key={rowKey} className="border-t border-slate-100">
                <div data-testid={`fuel-rider-${g.employee_id || i}`}
                  className={`${FGRID} hover:bg-slate-50/60 cursor-pointer text-sm`} onClick={() => setOpen(open === rowKey ? null : rowKey)}>
                  <div className="px-3 py-2.5 text-right min-w-0">
                    <span className="font-semibold text-slate-800">{g.name}</span>
                    {!g.matched && <span className="mr-1 text-[10px] rounded bg-amber-50 text-amber-600 px-1 py-0.5 whitespace-nowrap">{t("dash.fuel.noMatch")}</span>}
                    {g.orders ? <span data-testid={`fuel-rider-orders-${g.employee_id || i}`} className="mr-1 text-[10px] rounded bg-sky-50 text-sky-700 px-1 py-0.5 whitespace-nowrap"><bdi>{g.orders}</bdi> طلب{g.liters ? <> · <bdi>{(g.orders / g.liters).toFixed(2)}</bdi> طلب/لتر</> : null}</span> : null}
                  </div>
                  <div className="px-3 py-2.5 text-slate-500 font-mono-nums text-right truncate">{g.vehicle_number || "—"}</div>
                  <div className="px-3 py-2.5 font-mono-nums text-slate-600 text-center">{g.count}</div>
                  <div className="px-3 py-2.5 font-mono-nums text-slate-600 text-center">{g.liters}</div>
                  <div className="px-3 py-2.5 font-mono-nums font-bold text-emerald-700 text-center">{money(g.total)}</div>
                  <div className="px-3 py-2.5 flex justify-center"><ChevronDown className={`h-4 w-4 text-slate-400 transition ${open === rowKey ? "rotate-180" : ""}`} /></div>
                </div>
                {open === rowKey && (
                  <div className="bg-slate-50/50 px-4 py-2 border-t border-slate-100">
                    <div className="space-y-1">
                      {g.days.map((d, j) => {
                        const dup = g.days.some((x, k) => k !== j && x.date === d.date && Number(x.amount) === Number(d.amount) && (x.vehicle_number || "") === (d.vehicle_number || ""));
                        return (
                        <div key={d.id || j} data-testid={`fuel-op-${d.id}`} className={`flex items-center justify-between gap-2 text-xs text-slate-600 py-1 border-b border-slate-100 last:border-0 ${dup ? "bg-amber-50" : ""}`}>
                          <span className="font-mono-nums" dir="ltr">{d.date}{dup && <span className="ms-1 rounded bg-amber-200 px-1 text-[10px] font-bold text-amber-800">مكرر</span>}</span>
                          <span className="flex items-center gap-1"><Droplets className="h-3 w-3 text-sky-400" />{d.liters || 0} {t("dash.fuel.litersShort")}</span>
                          <span className="text-slate-400">{d.station || "—"}{d.split_days ? <span data-testid={`fuel-op-split-${d.id}`} className="ms-1 rounded bg-sky-100 px-1 text-[10px] font-bold text-sky-800">مقسّمة: {d.split_days} يوم من {money(d.split_total)}</span> : null}</span>
                          <span className="font-mono-nums font-semibold text-emerald-700">{money(d.amount)}</span>
                          {canEdit && d.id && <button type="button" title="حذف العملية" data-testid={`fuel-op-delete-${d.id}`} onClick={async (ev) => {
                            ev.stopPropagation();
                            if (!window.confirm(`حذف عملية ${d.date} بمبلغ ${d.amount}؟`)) return;
                            try { await api.fuelDeleteRecord(d.id); toast.success("تم حذف العملية"); load(month, provider, day); } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); }
                          }} className="rounded p-1 text-rose-500 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></button>}
                        </div>);
                      })}
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      </div>
        </TabsContent>
        <TabsContent value="report">
          <FuelReportPanel />
        </TabsContent>
        <TabsContent value="vehicles">
          <VehicleFuelReport canEdit={canEdit} />
        </TabsContent>
        <TabsContent value="waei">
          <WaeiAnalysisPanel canEdit={canEdit} />
        </TabsContent>
        <TabsContent value="settings"><FuelSettingsPanel /></TabsContent>
        <TabsContent value="payroll">
          <FuelPayrollDeduction canEdit={canEdit} />
        </TabsContent>
      </Tabs>
      <FuelPlateMatchDialog open={matchOpen} onOpenChange={setMatchOpen} month={month} provider={provider}
        onChanged={() => load(month || undefined, provider || undefined)} />
    </div>
  );
};
